import { useState, useEffect } from 'react';
import { RefreshCw, TrendingUp, DollarSign, Activity, UserCheck } from 'lucide-react';
import { campaignService } from '../services/campaignService';
import type { CampaignWithComputed } from '../types/campaign';
import { formatEuro } from '../utils/currency';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface CommissionsByEntity {
  name: string;
  totalCommission: number;
  campaignCount: number;
}

export function Commissions() {
  const { t } = useLanguage();
  const [byInfluencer, setByInfluencer] = useState<CommissionsByEntity[]>([]);
  const [byAgent, setByAgent] = useState<CommissionsByEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [commissionsOngoing, setCommissionsOngoing] = useState(0);
  const [commissionsInfluencer, setCommissionsInfluencer] = useState(0);

  useEffect(() => {
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    try {
      setLoading(true);
      const data = await campaignService.getAll();

      const paidByClientCampaigns = data.filter(campaign => {
        return campaign.company_invoice_status === 'Paid';
      });

      const influencerMap = new Map<string, { total: number; count: number }>();
      const agentMap = new Map<string, { total: number; count: number }>();
      let total = 0;

      paidByClientCampaigns.forEach(campaign => {
        const commission = campaign.commission_amount;
        total += commission;

        const influencer = campaign.influencer;
        if (influencer) {
          const current = influencerMap.get(influencer) || { total: 0, count: 0 };
          influencerMap.set(influencer, {
            total: current.total + commission,
            count: current.count + 1
          });
        }

        const agent = campaign.agent_name;
        if (agent) {
          const current = agentMap.get(agent) || { total: 0, count: 0 };
          agentMap.set(agent, {
            total: current.total + commission,
            count: current.count + 1
          });
        }
      });

      const influencerData: CommissionsByEntity[] = Array.from(influencerMap.entries())
        .map(([name, data]) => ({
          name,
          totalCommission: data.total,
          campaignCount: data.count
        }))
        .sort((a, b) => b.totalCommission - a.totalCommission);

      const agentData: CommissionsByEntity[] = Array.from(agentMap.entries())
        .map(([name, data]) => ({
          name,
          totalCommission: data.total,
          campaignCount: data.count
        }))
        .sort((a, b) => b.totalCommission - a.totalCommission);

      // Commissions Ongoing (campaigns not in Consultation or Completed status)
      const ongoingCampaigns = data.filter(campaign => {
        const status = campaign.campaign_status;
        return status !== 'Consultation' && status !== 'Completed';
      });

      let ongoingTotal = 0;
      ongoingCampaigns.forEach(campaign => {
        ongoingTotal += campaign.commission_amount;
      });

      // Commissions Influencer (campaigns in Billing status, sum of net_fee_influencer)
      const billingCampaigns = data.filter(campaign => {
        return campaign.campaign_status === 'Billing';
      });

      let influencerFeesTotal = 0;
      billingCampaigns.forEach(campaign => {
        influencerFeesTotal += campaign.net_fee_influencer;
      });

      setByInfluencer(influencerData);
      setByAgent(agentData);
      setTotalCommissions(total);
      setCommissionsOngoing(ongoingTotal);
      setCommissionsInfluencer(influencerFeesTotal);
    } catch (error) {
      log.error('Error loading commissions:', error);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
        <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
        <p className="text-gray-500 text-lg">{t('commissions.loadingData')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-4">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('commissions.overview')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('commissions.trackDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-xs text-emerald-700 font-medium uppercase">{t('commissions.totalReceived')}</p>
            </div>
            <p className="text-2xl font-bold text-emerald-900">{formatEuro(totalCommissions)}</p>
            <p className="text-xs text-emerald-600 mt-1">{t('commissions.totalReceivedDesc')}</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-xs text-blue-700 font-medium uppercase">{t('commissions.ongoing')}</p>
            </div>
            <p className="text-2xl font-bold text-blue-900">{formatEuro(commissionsOngoing)}</p>
            <p className="text-xs text-blue-600 mt-1">{t('commissions.ongoingDesc')}</p>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-xs text-amber-700 font-medium uppercase">{t('commissions.influencer')}</p>
            </div>
            <p className="text-2xl font-bold text-amber-900">{formatEuro(commissionsInfluencer)}</p>
            <p className="text-xs text-amber-600 mt-1">{t('commissions.influencerDesc')}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
            <h3 className="text-lg font-semibold text-blue-900">{t('commissions.byInfluencer')}</h3>
            <p className="text-xs text-blue-700 mt-0.5">{t('commissions.influencersCount').replace('{0}', String(byInfluencer.length))}</p>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('commissions.tableInfluencer')}
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('commissions.tableCampaigns')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('commissions.tableTotalCommission')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {byInfluencer.map((item, index) => (
                  <tr key={item.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-gray-700">
                      {item.campaignCount}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-right text-gray-900">
                      {formatEuro(item.totalCommission)}
                    </td>
                  </tr>
                ))}
                {byInfluencer.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                      {t('commissions.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
          <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
            <h3 className="text-lg font-semibold text-emerald-900">{t('commissions.byAgent')}</h3>
            <p className="text-xs text-emerald-700 mt-0.5">{t('commissions.agentsCount').replace('{0}', String(byAgent.length))}</p>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('commissions.tableAgent')}
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('commissions.tableCampaigns')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('commissions.tableTotalCommission')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {byAgent.map((item, index) => (
                  <tr key={item.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-4 py-2 text-sm text-center text-gray-700">
                      {item.campaignCount}
                    </td>
                    <td className="px-4 py-2 text-sm font-semibold text-right text-gray-900">
                      {formatEuro(item.totalCommission)}
                    </td>
                  </tr>
                ))}
                {byAgent.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                      {t('commissions.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
